import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe, NgClass, NgIf, NgTemplateOutlet, ViewportScroller } from '@angular/common';
import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatListItem, MatNavList } from '@angular/material/list';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer, MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar } from '@angular/material/toolbar';
import { ActivatedRoute, Event, Router, RouterLink, RouterLinkActive, RouterOutlet, Scroll } from '@angular/router';
import { Observable, Subscription, timer } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import ConfigJson from '../../config.json';
import { AuthService, Roles } from '../auth/auth.service';
import { MedicaidReportModalService } from '../medicaid/services/medicaid-report-modal.service';
import { AppPermissions } from '../permissions';
import { CreateStickyNoteModalComponent } from '../shared/components/create-sticky-note-modal/create-sticky-note-modal.component';
import { PageUnderEditHeaderComponent } from '../shared/components/page-under-edit-header/page-under-edit-header.component';
import { ScrollToTopComponent } from '../shared/components/scroll-to-top/scroll-to-top.component';
import { AuthForRolesDirective } from '../shared/directives/auth-for-roles.directive';
import { TimeoutModalComponent } from '../shared/modals/timeout-modal/timeout-modal.component';
import { IntakeType } from '../shared/models/case';
import { AchieveConfigService } from '../shared/services/achieve-config-service/achieve-config.service';
import { AutosaveService, AutosavingStates } from '../shared/services/autosave/autosave.service';
import { CaseService } from '../shared/services/case/case.service';
import { DatabaseLinksService } from '../shared/services/database-links/database-links.service';
import { FeatureFlagService } from '../shared/services/feature-flags/feature-flag.service';
import { FeatureFlags } from '../shared/services/feature-flags/feature-flags';
import { HelpModalConfig, HelpService } from '../shared/services/help/help.service';
import { SessionTimeoutService } from '../shared/services/session-timeout/session-timeout.service';
import { SignalRService } from '../shared/services/signal-r.service';
import { NewWindowConfig, openNewWindow, popupChecker } from '../shared/windowHelpers';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
  providers: [SignalRService],
  imports: [
    AsyncPipe,
    NgClass,
    NgIf,
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    MatAccordion,
    MatButton,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatIcon,
    MatIconButton,
    MatListItem,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatNavList,
    MatExpansionPanelTitle,
    MatSidenav,
    MatSidenavContainer,
    MatSidenavContent,
    MatToolbar,
    AuthForRolesDirective,
    PageUnderEditHeaderComponent,
    ScrollToTopComponent,
  ],
})
export class NavComponent implements OnInit, OnDestroy {
  private readonly autosaveService = inject(AutosaveService);
  private readonly sessionTimeoutService = inject(SessionTimeoutService);
  private subscription = new Subscription();
  private dialogRef: MatDialogRef<TimeoutModalComponent> | null = null;
  @ViewChild('drawer') drawer: MatDrawer;
  version = ConfigJson.version;
  @ViewChild('topNav') topNav: ElementRef;

  isVr: boolean;
  isInteractiveAccess: boolean;
  isFamily = false;
  isLearner = false;
  isEligibleFamily = false;
  isAea = false;
  hasTools = false;
  permissions = AppPermissions;
  isPopup = false;
  scrollPosition: number;
  drawerOpen = true;
  userFullName = '';
  userEmail = '';
  isMobile: boolean;
  readonly displayState = this.autosaveService.displayState;
  autosavingStates = AutosavingStates;
  intakeType: IntakeType;
  caseId: string;
  isDhh: boolean;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );

  @ViewChild('sideNavContent') mainContent: MatSidenavContent;

  get achieveSettings() {
    return this.achieveConfigService.settings;
  }

  get canAssignServiceCoordinator() {
    return this.authService.isAllowed(AppPermissions.AssignServiceCoordinator);
  }

  get canViewPendingReferrals() {
    return this.authService.isAllowed(AppPermissions.ViewPendingReferrals);
  }

  get canViewReferrals() {
    return (
      this.authService.isAllowed(AppPermissions.ViewReferrals) ||
      (this.intakeType !== IntakeType.PartB && this.authService.isAllowed(AppPermissions.PartCViewReferral))
    );
  }

  get onlyLeaUserManagerRoleAssigned() {
    return this.isLeaUserManager && this.authService.user?.rolePermissions?.length === 1;
  }

  get isReportingOnly() {
    return this.authService.user?.rolePermissions?.every((x) => x.role === Roles.ReportingOnly);
  }

  get isPortalUser() {
    return this.authService.isPortalUser;
  }

  get isVRManagementPhase2() {
    return this.featureFlagService.featureOn(FeatureFlags.IVRSPhase2);
  }

  get isVRManagement() {
    return this.authService.isSuperAdmin || this.authService.isAllowedStatewide(AppPermissions.VRManagement);
  }

  get isLeaUserManager() {
    return this.authService.user?.rolePermissions?.every((x) => x.role === Roles.LeaUserManager);
  }

  get isGeneralEducation() {
    return this.authService.user?.rolePermissions?.every((x) => x.role === Roles.GeneralEducation);
  }

  get canViewDhhRecheck() {
    return this.authService.isDhhAudiologist || this.authService.isDhhAudiometrist || this.authService.isSuperAdmin;
  }

  get canViewDhhScreening() {
    return this.authService.isDhhAudiologist || this.authService.isDhhAudiometrist || this.authService.isSuperAdmin;
  }

  get isDhhOnly() {
    return this.authService.isUserWithDhhOnlyRoles;
  }

  get canViewDashboard() {
    if (this.featureFlagService.featureOn(FeatureFlags.DhhDataDashboard)) {
      return true;
    }
    return (
      !this.authService.isUserWithDhhOnlyRoles ||
      (this.featureFlagService.featureOn(FeatureFlags.Dhh) &&
        (this.authService.isDhhAudiologist || this.authService.isDhhScheduler || this.authService.isDhhAudiometrist))
    );
  }

  get isDhhFeatureFlagOn() {
    return this.featureFlagService.featureOn(FeatureFlags.Dhh);
  }

  get canViewDhhReporting() {
    return this.authService.canViewDhhReporting;
  }

  get hasDhhStatewideSearchPermission() {
    return this.authService.hasDhhStatewideSearchPermission;
  }

  get isStickyNoteEnabled() {
    return !this.isPortalUser || this.featureFlagService.featureOn(FeatureFlags.EnableStickyNoteForPortalUsers);
  }

  get canMergePendingLearners() {
    return this.featureFlagService.featureOn(FeatureFlags.PendingLearnersMerge);
  }

  get isMedicaidReportingActive() {
    return this.featureFlagService.featureOn(FeatureFlags.MedicaidReporting);
  }

  FeatureFlags = FeatureFlags;
  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly viewportScroller: ViewportScroller,
    private readonly helpService: HelpService,
    private readonly caseService: CaseService,
    private dialog: MatDialog,
    private readonly achieveConfigService: AchieveConfigService,
    private readonly databaseLinksService: DatabaseLinksService,
    private medicaidReportModalService: MedicaidReportModalService,
    public readonly featureFlagService: FeatureFlagService
  ) {
    this.configureIdleTimeoutService();
  }

  private configureIdleTimeoutService() {
    const settings = this.achieveConfigService.settings;

    this.sessionTimeoutService.start({
      timeoutMinutes: settings.timeoutMinutes,
      warningSeconds: 120,
    });

    this.subscription.add(this.sessionTimeoutService.timeout$.subscribe(() => this.logout()));

    this.subscription.add(
      this.sessionTimeoutService.idleStart$.subscribe(() => {
        if (!this.dialogRef) {
          this.dialogRef = this.dialog.open(TimeoutModalComponent, { width: '748px' });
          this.subscription.add(this.dialogRef.afterClosed().subscribe(() => (this.dialogRef = null)));
        }
      })
    );

    this.subscription.add(this.sessionTimeoutService.interrupt$.subscribe(() => this.dialogRef?.close()));
  }

  ngOnInit() {
    this.subscription.add(
      this.router.events.pipe(filter((e: Event): e is Scroll => e instanceof Scroll)).subscribe((e) => {
        if (e.position) {
          // backward navigation
          this.viewportScroller.scrollToPosition(e.position);
        } else if (e.anchor) {
          // anchor navigation
          this.viewportScroller.scrollToAnchor(e.anchor);
        } else {
          // forward navigation
          this.mainContent.getElementRef().nativeElement.scrollTo(0, 0);
        }
      })
    );

    if (!!this.authService.user) {
      this.subscription.add(
        this.authService.authUserChanged$.subscribe(() => {
          if (this.authService.user.firstName && this.authService.user.lastName) {
            this.userFullName = `${this.authService.user.firstName} ${this.authService.user.lastName}`;
          } else if (this.authService.user.firstName) {
            this.userFullName = this.authService.user.firstName;
          }
          this.userEmail = this.authService.user.email;
        })
      );
      this.authService.authUserChanged();

      this.isVr = this.featureFlagService.featureOn(FeatureFlags.Ivrs) && this.authService.isVrUser;
      this.isInteractiveAccess =
        this.featureFlagService.featureOn(FeatureFlags.DisputeResolution) && this.authService.isInteractiveAccessUser;
      this.isFamily = this.authService.isFamily;
      this.isLearner = this.authService.isLearner;
      this.isEligibleFamily = this.authService.isEligibleFamily;
      this.isAea = !this.isLearner && !this.isFamily && !this.isEligibleFamily;
      this.isDhh = this.authService.isDhhUser || this.authService.isSuperAdmin;
    }

    this.subscription.add(
      this.isHandset$.subscribe((d: boolean) => {
        this.isMobile = d;

        if (d) {
          this.drawerOpen = false;
        }
      })
    );

    this.isPopup = popupChecker(this.route);
    this.hasTools = this.featureFlagService.featureOn(FeatureFlags.MyTools);
  }

  onOpenEconsent() {
    // const formKey = ConsentFormType.FiieConsent // InitialFamilyEvaluationAndAssessment;
    // this.mapConsentFormTypeToFormKey(consentForm.type);
    // const config: NewWindowConfig = {
    //   path: `/consent-signature`,
    //   params: {
    //     learnerId: 'ca5a7774-056d-4fe1-beb4-adcc012b212d', // consentForm.learnerId,
    //     caseId: 'd8bdc376-b915-4ca3-8b68-adcc012b25bb', // consentForm.caseId,
    //     consentFormId: 'd2621331-510c-46f7-b60e-adcc012b6176', // consentForm.id,
    //     formKey: 'ConsentForFullAndIndividualEvaluation',
    /*
         learnerId: '15E3C27E-3761-4D1C-A889-ADB900D32A9A', //consentForm.learnerId,
         caseId: 'F33B24E5-6DDD-459F-B397-ADB900D32A8E', //consentForm.caseId,
         consentFormId: '15E3C27E-3761-4D1C-A889-ADB900D32A9A', //consentForm.id,
        */
    //   },
    //   popup: true,
    //   isPreview: true,
    //   width: '1450px',
    // };
    // openNewWindow(config);
  }

  toggleDrawer() {
    this.drawer.toggle();
    this.drawerOpen = this.drawer.opened;
  }

  openMedicaidReport() {
    this.medicaidReportModalService.openReportModal();
  }

  async logout() {
    this.authService.logout(
      this.isVr
        ? '/auth/ivrs/login'
        : this.isInteractiveAccess
          ? '/auth/interactive-access/login'
          : this.isFamily || this.isLearner
            ? '/auth/family/login'
            : ''
    );
  }

  onScroll(e) {
    this.scrollPosition = e.target.scrollTop;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    if (!this.drawer) {
      return;
    }
    timer(50)
      .pipe(take(1))
      .subscribe(() => (this.drawerOpen = this.drawer.opened));
  }

  onOpenHelp() {
    const dictionary = this.helpService.getAllDictionaries();
    this.helpService.openHelpModal({
      help: dictionary,
      canBrowse: true,
    } as HelpModalConfig);
  }

  onOpenCreateStickyNote() {
    this.dialog.open(CreateStickyNoteModalComponent, { width: '748px' });
  }

  onOpenStickyNotes() {
    const config: NewWindowConfig = {
      path: 'sticky-notes/list',
      popup: true,
    };
    openNewWindow(config);
  }

  switchVersion(version: string) {
    const url = ['/'];
    if (version === 'ivrs') {
      this.isVr = true;
      url.push('ivrs');
    } else if (version === 'achieve') {
      this.isVr = false;
    }
    this.router.navigate(url);
  }

  async openPLS() {
    const token = await this.authService.getToken();
    const plsUrl = this.databaseLinksService.getDatabaseLink('plsUrl');
    window.open(`${plsUrl}/?app=achieve&token=${token}`, '_blank');
  }

  async openGeneralGuide() {
    window.open(`${this.databaseLinksService.getDatabaseLink('generalNavigationUserGuide')}`, '_blank');
  }

  async openEarlyAccessGuide() {
    window.open(`${this.databaseLinksService.getDatabaseLink('earlyAccessSystemUserGuide')}`, '_blank');
  }

  async openSpecialEducationGuide() {
    window.open(`${this.databaseLinksService.getDatabaseLink('specialEducationSystemUserGuide')}`, '_blank');
  }

  ngOnDestroy(): void {
    this.sessionTimeoutService.stop();
    this.subscription.unsubscribe();
  }
}
